// Converts deal rows to commission-engine inputs and runs the whole calculation.
// Pure: no database access, so the deal page can recalculate live as Brett types.

import {
  analyzeLease,
  calculateFees,
  CommissionInputError,
  leaseExpiration,
  planPayments,
  type CashSplit,
  type DealDates,
  type FeeComponent,
  type FeeTotals,
  type LeaseAnalysis,
  type LeaseInput,
  type Payout,
  type PaymentPlan,
  type Trigger,
} from './commission';
import type { Tables } from './supabase';

export type DealRow = Tables<'deals'>;
export type LeaseTermsRow = Tables<'deal_lease_terms'>;
export type AbatementRow = Pick<Tables<'deal_rent_abatements'>, 'start_month' | 'months' | 'pct'>;
export type FeeRow = Pick<Tables<'fee_components'>, 'component_key' | 'label' | 'basis' | 'rate' | 'payer' | 'split_pct' | 'override_amount'>;
export type PayoutRow = Pick<
  Tables<'payout_structures'>,
  'kind' | 'cash_splits' | 'annuity_monthly_amount' | 'annuity_months' | 'annuity_trigger' | 'annuity_offset_days' | 'annuity_custom_date' | 'closing_offset_days'
>;

export const num = (v: number | string | null | undefined): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

// ---------------------------------------------------------------------------
// Rows -> engine inputs
// ---------------------------------------------------------------------------

export function toDealDates(d: Pick<DealRow, 'lease_executed_date' | 'commencement_estimated_date' | 'commencement_confirmed_date' | 'occupancy_date' | 'contract_effective_date' | 'dd_end_date' | 'closing_date'>): DealDates {
  return {
    leaseExecuted: d.lease_executed_date,
    commencementEstimated: d.commencement_estimated_date,
    commencementConfirmed: d.commencement_confirmed_date,
    occupancy: d.occupancy_date,
    contractEffective: d.contract_effective_date,
    ddEnd: d.dd_end_date,
    closing: d.closing_date,
  };
}

/** Null until enough is entered to describe the lease (term and starting rent, plus SF when rent is $/SF). */
export function toLeaseInput(t: Partial<LeaseTermsRow> | null | undefined, abatements: AbatementRow[]): LeaseInput | null {
  if (!t) return null;
  const term = num(t.term_months);
  const rent = num(t.base_rent_value);
  const sf = num(t.rentable_sf) ?? 0;
  if (!term || !rent || !t.base_rent_mode) return null;
  if (t.base_rent_mode === 'psf_year' && !sf) return null;
  const esc = t.escalation_kind ?? 'none';
  return {
    rentableSf: sf,
    termMonths: term,
    baseRent: { mode: t.base_rent_mode as 'psf_year' | 'monthly', value: rent },
    escalation:
      esc === 'percent'
        ? { kind: 'percent', annualPct: num(t.escalation_value) ?? 0 }
        : esc === 'fixed_psf'
          ? { kind: 'fixed_psf', annualPsf: num(t.escalation_value) ?? 0 }
          : esc === 'custom'
            ? { kind: 'custom', mode: (t.custom_schedule_mode ?? 'monthly') as 'psf_year' | 'monthly', values: (t.custom_schedule ?? []).map(Number) }
            : { kind: 'none' },
    abatements: abatements.map((a) => ({ startMonth: a.start_month, months: a.months, pct: Number(a.pct) })),
    opex: { enabled: !!t.opex_enabled, psfYear: num(t.opex_psf_year) },
  };
}

export function toFeeComponents(rows: FeeRow[]): FeeComponent[] {
  return rows.map((r) => ({
    key: r.component_key,
    label: r.label ?? undefined,
    basis: r.basis as FeeComponent['basis'],
    rate: num(r.rate),
    payer: r.payer as FeeComponent['payer'],
    splitPct: num(r.split_pct),
    overrideAmount: num(r.override_amount),
  }));
}

export function toPayout(p: PayoutRow | null | undefined, isLease: boolean): Payout {
  if (!p) return isLease ? { kind: 'cash_out', splits: [{ trigger: 'lease_execution', pct: 50 }, { trigger: 'commencement', pct: 50 }] } : { kind: 'at_closing' };
  if (p.kind === 'annuity') {
    return {
      kind: 'annuity',
      monthlyAmount: num(p.annuity_monthly_amount) ?? 0,
      months: p.annuity_months ?? 0,
      trigger: (p.annuity_trigger ?? 'occupancy') as Trigger,
      offsetDays: p.annuity_offset_days,
      customDate: p.annuity_custom_date,
    };
  }
  if (p.kind === 'at_closing') return { kind: 'at_closing', offsetDays: p.closing_offset_days };
  return { kind: 'cash_out', splits: ((p.cash_splits as unknown as CashSplit[] | null) ?? []).map((s) => ({ ...s, pct: Number(s.pct) })) };
}

// ---------------------------------------------------------------------------
// The whole calculation for one deal
// ---------------------------------------------------------------------------

export interface DealCalc {
  lease: LeaseAnalysis | null;
  leaseError: string | null;
  fees: FeeTotals | null;
  feeError: string | null;
  plan: PaymentPlan | null;
  planError: string | null;
  expiration: { date: string | null; basis: 'estimated' | 'confirmed' | null };
}

function message(err: unknown): string {
  return err instanceof CommissionInputError || err instanceof Error ? err.message : String(err);
}

export function calculateDeal(input: {
  isLease: boolean;
  dates: DealDates;
  leaseTerms: Partial<LeaseTermsRow> | null;
  abatements: AbatementRow[];
  fees: FeeRow[];
  payout: PayoutRow | null;
  purchasePrice: number | null;
}): DealCalc {
  const out: DealCalc = { lease: null, leaseError: null, fees: null, feeError: null, plan: null, planError: null, expiration: { date: null, basis: null } };
  const leaseInput = input.isLease ? toLeaseInput(input.leaseTerms, input.abatements) : null;
  if (leaseInput) {
    try {
      out.lease = analyzeLease(leaseInput);
    } catch (err) {
      out.leaseError = message(err);
    }
  }
  if (input.isLease) out.expiration = leaseExpiration(input.dates, num(input.leaseTerms?.term_months));
  try {
    out.fees = calculateFees(toFeeComponents(input.fees), { lease: out.lease, price: input.purchasePrice, sf: num(input.leaseTerms?.rentable_sf) });
  } catch (err) {
    out.feeError = message(err);
  }
  if (out.fees) {
    try {
      out.plan = planPayments(out.fees.net, toPayout(input.payout, input.isLease), input.dates);
    } catch (err) {
      out.planError = message(err);
    }
  }
  return out;
}

